import {useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {HelpIcon} from '@/components/help-icon'
import {AgregadoBloque} from '@/components/bateria/agregado-bloque'
import {SegmentosEnRiesgo} from '@/components/bateria/nivel-charts'
import {VistaPills} from '@/components/bateria/vista-pills'
import {type AgregadoCuestionario, type Agregados} from '@/hooks/bateria/use-bateria'
import {prettifyTaxonomia} from '@/lib/bateria'
import type {EntradaSegmento} from '@/lib/bateria-charts'

type GroupKey =
  | 'area'
  | 'cargo'
  | 'sede'
  | 'vinculacion'
  | 'sexo'
  | 'nivel_estudios'
  | 'estrato'
  | 'tipo_vivienda'
  | 'tipo_cargo'
  | 'tipo_contrato'
  | 'tipo_salario'
  | 'rango_edad'
  | 'antiguedad_empresa'
type VistaKey = 'consolidado' | 'A' | 'B' | GroupKey

export type GroupVista = {
  key: GroupKey
  data: Record<string, AgregadoCuestionario>
  nPor: Record<string, number>
  labelFor?: (key: string) => string
}

function directAgregado(agregados: Agregados, vista: VistaKey): AgregadoCuestionario | null {
  if (vista === 'consolidado') return agregados.consolidado
  if (vista === 'A' || vista === 'B') return agregados.por_forma[vista] ?? null
  return null
}

function directN(agregados: Agregados, vista: VistaKey): number {
  if (vista === 'consolidado') return agregados.n_validados.total ?? 0
  if (vista === 'A' || vista === 'B') return agregados.n_validados[vista] ?? 0
  return 0
}

/** Cambiar entre los tres alcances directos no re-monta `AgregadoBloque`: así ApexCharts anima la
 * transición en vez de repintar de golpe. */
export function ResultadosSegmentador({agregados, groupVistas}: {agregados: Agregados; groupVistas: GroupVista[]}) {
  const {t} = useTranslation()
  const [vista, setVista] = useState<VistaKey>('consolidado')
  const [drill, setDrill] = useState<string | null>(null)

  const groupByKey = new Map(groupVistas.map((g) => [g.key, g]))
  const vistas = [
    {key: 'consolidado' as VistaKey, label: t('admin.bateria.resultados.consolidado')},
    ...(['A', 'B'] as const).map((forma) => ({
      key: forma as VistaKey,
      label: t('admin.bateria.resultados.formaTab', {forma}),
      // Disabled, not hidden, when empty: the A/B pair is a fixed axis.
      disabled: (agregados.n_validados[forma] ?? 0) === 0,
    })),
    ...groupVistas.map((g) => ({key: g.key as VistaKey, label: t(`admin.bateria.resultados.vista.${g.key}`)})),
  ]

  function pick(key: VistaKey) {
    setVista(key)
    setDrill(null)
  }

  const group = groupByKey.get(vista as GroupKey)
  const direct = directAgregado(agregados, vista)

  const vistaDirecta = vista === 'consolidado' || vista === 'A' || vista === 'B'

  return (
    <div className="space-y-4" data-testid="resultados-segmentador">
      <VistaPills
        label={t('admin.bateria.resultados.vistaLabel')}
        testIdPrefix="vista"
        valor={vista}
        onPick={pick}
        opciones={vistas}>
        {/* T11: el rótulo del disparador es contextual; «Ver ayuda» repetido deja botones mudos. */}
        <HelpIcon
          label={t('common.help.iconLabelFor', {campo: t('admin.bateria.resultados.vistaLabel')})}
          testId="vista-help">
          {vistaDirecta ? t('admin.bateria.resultados.tabsHelp') : t('admin.bateria.resultados.grupoVistaHelp')}
        </HelpIcon>
      </VistaPills>

      {group ? (
        <GroupVistaView group={group} drill={drill} onDrill={setDrill} onClearDrill={() => setDrill(null)} />
      ) : direct ? (
        <AgregadoBloque agregado={direct} segmentTotalN={directN(agregados, vista)} />
      ) : (
        <p className="text-sm text-muted-foreground">{t('admin.bateria.resultados.sinValidos')}</p>
      )}
    </div>
  )
}

function GroupVistaView({
  group,
  drill,
  onDrill,
  onClearDrill,
}: {
  group: GroupVista
  drill: string | null
  onDrill: (key: string) => void
  onClearDrill: () => void
}) {
  const {t} = useTranslation()
  const labelFor = useMemo(
    () => group.labelFor ?? ((key: string) => (key.startsWith('(') ? key : prettifyTaxonomia(key))),
    [group]
  )
  // Memoizada para que ApexCharts anime; su estabilidad depende del memo del `group` en el llamador.
  const segmentos = useMemo<EntradaSegmento[]>(
    () =>
      Object.entries(group.data).map(([key, agregado]) => ({
        clave: key,
        label: labelFor(key),
        agregado,
        n: group.nPor[key] ?? 0,
      })),
    [group, labelFor]
  )
  const drilled = drill ? group.data[drill] : undefined

  return (
    <div className="space-y-4">
      <div className="surface p-4">
        <p className="mb-2 font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
          {t('admin.bateria.resultados.comparacionSegmentosLabel')}
        </p>
        <SegmentosEnRiesgo
          segmentos={segmentos}
          ariaLabel={t('admin.bateria.resultados.comparacionSegmentosLabel')}
          seleccionado={drill}
          onPick={onDrill}
        />
      </div>

      {drilled && (
        <section className="space-y-3" data-testid="segmento-detalle">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-heading text-base text-foreground">{labelFor(drill ?? '')}</h3>
            <button
              type="button"
              onClick={onClearDrill}
              data-testid="segmento-cerrar"
              className="font-mono text-[11px] tracking-wider text-primary uppercase outline-none hover:underline focus-visible:ring-2 focus-visible:ring-ring/50">
              {t('admin.bateria.resultados.cerrarSegmento')}
            </button>
          </div>
          <AgregadoBloque agregado={drilled} segmentTotalN={drill ? group.nPor[drill] : undefined} />
        </section>
      )}
    </div>
  )
}
